import { defineStore } from "pinia";
import { withErrorMessage } from "~/helpers/withErrorMessage";
import { withErrorHandling } from "~/helpers/withErrorHandling";
import { withLoading } from "~/helpers/withLoading";
import type {
  WebModelGetDevice,
  WebModelGetDeviceCounts,
  WebModelPostNewDevice,
} from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import {
  exportCompanyDeviceData,
  exportUserDeviceData,
  findDevices,
  getDeviceCounts,
  makeDevicePrivate,
  makeDevicePublic,
} from "~/model/api/pLCnextStoreFrontendAPI";
import { useUserStore } from "~/stores/userstore";
import { addDevice } from "~/model/multipartRequests";

interface State {
  userDevices: WebModelGetDevice[];
  companyDevices: WebModelGetDevice[];
  memberDevices: WebModelGetDevice[];
  deviceCounts: WebModelGetDeviceCounts | undefined;
}

export const useDevicesStore = defineStore("devices", {
  // convert to a function
  state: (): State => ({
    userDevices: [],
    companyDevices: [],
    memberDevices: [],

    deviceCounts: undefined,
  }),
  getters: {
    memberDevicesWithoutUserDevices: (state): WebModelGetDevice[] => {
      const userStore = useUserStore();
      return (
        (state.memberDevices ?? [])
          // filter out devices which belong to the user and devices which are public
          // so only show private devices which do not belong to the user
          .filter(
            (device) =>
              device.userUuid && device.userUuid !== userStore.authUser?.uuid,
          )
      );
    },
    getUserDevice:
      (state) =>
      (deviceUuid: string): WebModelGetDevice | undefined => {
        return state.userDevices.find(
          (device) => device.deviceId === deviceUuid,
        );
      },
  },
  actions: {
    async loadUserDevices() {
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(findDevices())),
      );
      console.debug("loadUserDevices: ", data, error);
      if (data) {
        this.userDevices = data;
      }
    },
    async loadCompanyDevices() {
      const userStore = useUserStore();
      const { data, error } = await withErrorHandling(
        withErrorMessage(
          withLoading(
            findDevices({
              companyId: userStore.authUser?.companyId,
              public: true,
            }),
          ),
        ),
      );
      console.debug("loadCompanyDevices: ", data, error);
      if (data) {
        this.companyDevices = data;
      }
    },
    async loadMemberDevices() {
      const userStore = useUserStore();
      const { data, error } = await withErrorHandling(
        withErrorMessage(
          withLoading(
            findDevices({
              companyId: userStore.authUser?.companyId,
              public: false,
            }),
          ),
        ),
      );
      console.debug("loadMemberDevices: ", data, error);
      if (data) {
        this.memberDevices = data;
      }
    },
    async moveDeviceToCompany(deviceUuid: string) {
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(makeDevicePublic(deviceUuid))),
      );
      console.debug("moveDeviceToCompany: ", data, error);
      await this.loadUserDevices();
      if (useUserStore().isCompanyAdmin) {
        await this.loadMemberDevices();
      }
    },

    async loadDevicesCount() {
      if (!useUserStore().authUserUuid) {
        console.debug("loadDevicesCount: No auth user UUID, skipping.");
        this.deviceCounts = undefined;
        return;
      }
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(getDeviceCounts())),
      );
      console.debug("loadDevicesCount: ", data, error);
      if (data) {
        this.deviceCounts = data;
      }
    },

    async moveDeviceToUser(deviceUuid: string) {
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(makeDevicePrivate(deviceUuid))),
      );
      console.debug("moveDeviceToUser: ", data, error);
      await this.loadCompanyDevices();
    },

    async addDevice(
      webModelPostNewDevice: WebModelPostNewDevice,
      iconFile: Blob | undefined = undefined,
    ) {
      const { data } = await withErrorMessage(
        withLoading(addDevice({ json: webModelPostNewDevice, iconFile })),
      );
      if (data) {
        return true;
      }
      return false;
    },

    async exportUserDeviceData(userId: string) {
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(exportUserDeviceData(userId))),
      );
      console.debug("exportUserDeviceData", data, error);
      if (!error) {
        return data;
      }
      return false;
    },

    async exportCompanyDeviceData(companyId: string) {
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(exportCompanyDeviceData(companyId))),
      );
      console.debug("exportCompanyDeviceData", data, error);
      if (!error) {
        return data;
      }
      return false;
    },

    // easily reset state using `$reset`
    clear() {
      this.$reset();
    },
  },
});
